import type { ComponentProps } from 'react';

import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { FormFieldLabel } from './form-field-label';
import { FormFieldMessages } from './form-field-messages';
import type { BaseFormFieldProps } from './shared';
import { useFormField } from './shared';

interface CheckboxInputProps extends ComponentProps<typeof Checkbox>, BaseFormFieldProps {
  checkboxClassName?: string;
  description?: string;
}

function CheckboxInput({
  className,
  label,
  helperText,
  helperTextPlacement = 'beforeControl',
  description,
  isRequired,
  id,
  showAsterisk,
  checkboxClassName,
  errorText,
  invalid,
  disabled,
  ref,
  'aria-label': ariaLabel,
  ...props
}: CheckboxInputProps) {
  const effectiveHelperText = helperText ?? description;
  const { fieldId, helperId, errorId, describedBy } = useFormField(
    id,
    effectiveHelperText,
    errorText,
  );
  const isInvalid = Boolean(invalid || errorText);
  const hasStackedContent = Boolean(effectiveHelperText || errorText);

  if (helperTextPlacement === 'afterControl') {
    return (
      <div className={cn('grid gap-1.5', className)}>
        <div className="flex items-center gap-3">
          <Checkbox
            id={fieldId}
            ref={ref}
            aria-required={isRequired}
            aria-invalid={isInvalid || undefined}
            aria-describedby={describedBy}
            aria-label={ariaLabel ?? label}
            className={checkboxClassName}
            disabled={disabled}
            {...props}
          />
          <div
            className={cn(
              'grid cursor-pointer gap-1.5 leading-none font-normal',
              disabled && 'cursor-not-allowed opacity-50',
            )}
          >
            {label && (
              <FormFieldLabel
                htmlFor={fieldId}
                label={label}
                isRequired={isRequired}
                showAsterisk={showAsterisk}
              />
            )}
          </div>
        </div>
        {effectiveHelperText && (
          <div className="ml-7">
            <FormFieldMessages helperText={effectiveHelperText} helperId={helperId} />
          </div>
        )}
        {errorText && (
          <div className="ml-7">
            <FormFieldMessages errorText={errorText} errorId={errorId} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn('flex gap-3', hasStackedContent ? 'items-start' : 'items-center', className)}
    >
      <Checkbox
        id={fieldId}
        ref={ref}
        aria-required={isRequired}
        aria-invalid={isInvalid || undefined}
        aria-describedby={describedBy}
        aria-label={ariaLabel ?? label}
        className={checkboxClassName}
        disabled={disabled}
        {...props}
      />
      <div
        className={cn(
          'grid cursor-pointer gap-1.5 leading-none font-normal',
          disabled && 'cursor-not-allowed opacity-50',
        )}
      >
        {label && (
          <FormFieldLabel
            htmlFor={fieldId}
            label={label}
            isRequired={isRequired}
            showAsterisk={showAsterisk}
          />
        )}
        {effectiveHelperText && (
          <FormFieldMessages helperText={effectiveHelperText} helperId={helperId} />
        )}
        {errorText && <FormFieldMessages errorText={errorText} errorId={errorId} />}
      </div>
    </div>
  );
}

export { CheckboxInput };
